/**
 * Payment reconciliation (ADMIN-04): provider webhook events vs the platform
 * ledger. Pure; amounts are integer paise.
 *
 * Matching, in order:
 *   1. by provider reference (event.id === entry.provider_ref)
 *   2. otherwise by (order_ref, kind, amount) for ledger rows posted without a reference
 * Ledger rows with kind 'commission' or 'adjustment' are platform-internal and never
 * expected at the provider.
 */

/** Provider event type → ledger kind (a registry, not a switch). */
export const EVENT_KIND = {
  'payment.captured': 'payment',
  'refund.processed': 'refund',
  'transfer.processed': 'transfer',
  'transfer.reversed': 'reversal',
};

export const INTERNAL_KINDS = new Set(['commission', 'adjustment']);

/** Result status → label + badge variant, shared by the screen and the CSV. */
export const RECON_STATUS = {
  matched: { label: 'Matched', variant: 'success' },
  amount_mismatch: { label: 'Amount mismatch', variant: 'error' },
  missing_in_ledger: { label: 'Missing in ledger', variant: 'warning' },
  missing_at_provider: { label: 'Missing at provider', variant: 'warning' },
};

const kindOf = (event) => EVENT_KIND[event.type] || 'unknown';

/**
 * @returns {{ rows: Array, totals: { provider, ledger, matched, unmatchedProvider, unmatchedLedger, mismatchDelta, openCount } }}
 * Each row: { key, status, kind, order_ref, merchant_slug, provider_id, ledger_id, provider_paise, ledger_paise, delta_paise, at }
 */
export function reconcile(events = [], entries = []) {
  const external = entries.filter((e) => !INTERNAL_KINDS.has(e.kind));
  const used = new Set();
  const byRef = new Map();
  for (const e of external) if (e.provider_ref) byRef.set(e.provider_ref, e);

  const rows = [];
  for (const ev of events) {
    const kind = kindOf(ev);
    let entry = byRef.get(ev.id);
    if (entry && used.has(entry.id)) entry = undefined;
    if (!entry) {
      entry = external.find(
        (e) => !used.has(e.id) && !e.provider_ref && e.order_ref === ev.order_ref && e.kind === kind && e.amount_paise === ev.amount_paise
      );
    }
    if (entry) used.add(entry.id);
    const delta = entry ? entry.amount_paise - ev.amount_paise : null;
    rows.push({
      key: `ev:${ev.id}`,
      status: !entry ? 'missing_in_ledger' : delta === 0 ? 'matched' : 'amount_mismatch',
      kind,
      order_ref: ev.order_ref,
      merchant_slug: ev.merchant_slug,
      provider_id: ev.id,
      ledger_id: entry ? entry.id : null,
      provider_paise: ev.amount_paise,
      ledger_paise: entry ? entry.amount_paise : null,
      delta_paise: delta,
      at: ev.received_at,
    });
  }
  for (const e of external) {
    if (used.has(e.id)) continue;
    rows.push({
      key: `le:${e.id}`,
      status: 'missing_at_provider',
      kind: e.kind,
      order_ref: e.order_ref,
      merchant_slug: e.merchant_slug,
      provider_id: e.provider_ref || null,
      ledger_id: e.id,
      provider_paise: null,
      ledger_paise: e.amount_paise,
      delta_paise: null,
      at: e.posted_at,
    });
  }

  const sum = (list, f) => list.reduce((a, r) => a + (r[f] || 0), 0);
  const of = (s) => rows.filter((r) => r.status === s);
  const totals = {
    provider: sum(events, 'amount_paise'),
    ledger: sum(external, 'amount_paise'),
    matched: sum(of('matched'), 'provider_paise'),
    unmatchedProvider: sum(of('missing_in_ledger'), 'provider_paise'),
    unmatchedLedger: sum(of('missing_at_provider'), 'ledger_paise'),
    mismatchDelta: of('amount_mismatch').reduce((a, r) => a + Math.abs(r.delta_paise), 0),
    openCount: rows.filter((r) => r.status !== 'matched').length,
  };
  return { rows, totals };
}

/** Sum ledger rows of one kind, e.g. platform commission for the period. */
export function totalOfKind(entries = [], kind) {
  return entries.filter((e) => e.kind === kind).reduce((a, e) => a + e.amount_paise, 0);
}
